import { addHours, isPast, isBefore } from 'date-fns';

/**
 * Check if a time slot is bookable based on calendar configuration
 * @param {Date} slotTime - The time slot to check
 * @param {Object} calendarConfig - Calendar configuration object
 * @param {Function} t - Translation function (optional)
 * @returns {Object} { isBookable: boolean, reason: string }
 */
export const isSlotBookable = (slotTime, calendarConfig, t = null) => {
  const now = new Date();

  // Check if slot is in the past
  if (isPast(slotTime) || isBefore(slotTime, now)) {
    return {
      isBookable: false,
      reason: t ? t('booking.slotInPast') : 'This time slot is in the past'
    };
  }

  // Check min_notice_hours
  const minNoticeHours = calendarConfig?.booking_settings?.min_notice_hours || 0;
  if (minNoticeHours > 0) {
    const minBookingTime = addHours(now, minNoticeHours);
    if (isBefore(slotTime, minBookingTime)) {
      return {
        isBookable: false,
        reason: t ? t('booking.requiresNotice', { hours: minNoticeHours }) : `Requires at least ${minNoticeHours} hours notice`
      };
    }
  }

  // Check advance_booking_days (not too far in the future)
  const advanceBookingDays = calendarConfig?.booking_settings?.advance_booking_days || 30;
  const maxBookingTime = addHours(now, advanceBookingDays * 24);
  if (isBefore(maxBookingTime, slotTime)) {
    return {
      isBookable: false,
      reason: t ? t('booking.advanceBookingLimit', { days: advanceBookingDays }) : `Can only book up to ${advanceBookingDays} days in advance`
    };
  }

  return {
    isBookable: true,
    reason: null
  };
};

/**
 * Get tooltip message for unbookable slots
 * @param {Date} slotTime - The time slot
 * @param {Object} calendarConfig - Calendar configuration
 * @param {Function} t - Translation function (optional)
 * @returns {string} Tooltip message
 */
export const getSlotTooltip = (slotTime, calendarConfig, t = null) => {
  const { isBookable, reason } = isSlotBookable(slotTime, calendarConfig, t);
  
  if (!isBookable) {
    return reason;
  }
  
  return t ? t('booking.clickToBookSlot') : 'Click to book this time slot';
};
